import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Offices, SelectOptions } from '@etoh/database/core';
import {
  FormlyFieldConfig,
  FormlyFormOptions,
  FormlyModule,
} from '@ngx-formly/core';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzMessageService } from 'ng-zorro-antd/message';
import { Observable, combineLatest, map, switchMap, take } from 'rxjs';
import { ContactAddComponent } from '../../../features/contacts/containers/contact-add/contact-add.component';
import { StoreService } from '../../../features/store.service';
import { UserService } from '../../../user.service';
import { userForm } from '../../forms/user.form';

@Component({
  selector: 'etoh-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FlexLayoutModule,
    NzFormModule,
    ReactiveFormsModule,
    FormlyModule,
    CommonModule,
    NzButtonModule,
    ContactAddComponent,
  ],
})
export class UserProfileComponent implements OnInit {
  loading = false;

  form = new FormGroup({});
  model: any = {};
  options: FormlyFormOptions = {};

  fields: FormlyFieldConfig[] = userForm;

  firebaseUser$ = this.afAuth.authState;

  office$ = this.userService.userOfficeId$.pipe(
    switchMap((officeId) => {
      return this.storeService.getObjectFromStoreById<Offices>(
        'offices',
        this.model.officeId
      );
    })
  );

  extraInformations$: Observable<SelectOptions[]>;

  constructor(
    public userService: UserService,
    private message: NzMessageService,
    private cd: ChangeDetectorRef,
    private afAuth: AngularFireAuth,
    private storeService: StoreService
  ) {}

  ngOnInit(): void {
    this.userService.user$.pipe(take(1)).subscribe((user) => {
      this.model = user;
    });

    this.afAuth.authState.subscribe((user) => {
      if (user) {
        this.model.email = user.email;
      }
    });

    this.extraInformations$ = combineLatest([
      this.userService.user$,
      this.office$,
      this.firebaseUser$,
    ]).pipe(
      map(([user, office, firebaseUser]) => {
        return [
          {
            label: 'User id',
            value: user?.id,
          },
          {
            label: 'Email',
            value: [
              firebaseUser?.email,
              firebaseUser?.emailVerified ? 'Verified' : 'Not verified',
            ].join(' - '),
          },
          {
            label: 'Role',
            value: user?.role,
          },
          {
            label: 'Office',
            value: office?.name,
          },
          {
            label: 'Company',
            value: user?.contact?.company?.name,
          },
        ].filter((item) => item.value);
      })
    );
  }

  public async submit() {
    this.loading = true;

    const userId = this.userService.userId$.value as string;

    this.userService.updateUserById(userId, this.form.value).subscribe({
      next: (user) => {
        this.message.create('success', `Profile updated successfully`);

        this.loading = false;
        this.cd.detectChanges();
      },
      error: (err) => {
        this.loading = false;
        this.cd.detectChanges();
      },
    });
  }
}
